import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { TournamentTypeString } from '../../../enums/enums';
import CustomButton from '../../common/Button';
import Modal from '../../common/Modal';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { TrophyFilled } from '@ant-design/icons';
import { ParticipantScoreDto } from '../../../types/types';
import { parseScoreData } from '../../../utils/eventUtils';

interface ScoreCardProps {
  participantDetails: ParticipantScoreDto | undefined;
  tournamentType: string;
  participantNumber: number;
  sportName: string;
  tournamentMatchId: number;
  onDeclaration: ({ winnerId }: { winnerId: number }) => void;
  onScoreChange: (jsonScore: string) => void;
  isMatchCompleted: boolean;
  winnerId?: number | null;
  /** When true, the Declare Winner action is disabled (e.g. registration open / stale bracket). */
  disableDeclare?: boolean;
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  participantDetails,
  tournamentType,
  participantNumber,
  sportName,
  onDeclaration,
  isMatchCompleted,
  winnerId,
  disableDeclare = false,
}) => {
  const [isDeclareWinnerModalOpen, setIsDeclareWinnerModalOpen] =
    useState(false);
  const isWinner =
    isMatchCompleted && winnerId === participantDetails?.participantId;

  const scoreData = useMemo(() => {
    return participantDetails?.scoreData
      ? parseScoreData(participantDetails?.scoreData, sportName)
      : null;
  }, [participantDetails, sportName]);

  const teamName = useMemo(() => {
    const p1 = participantDetails?.participantName?.trim();
    const p2 = participantDetails?.partnerName?.trim();
    const showPartner =
      tournamentType !== TournamentTypeString.Singles &&
      tournamentType !== TournamentTypeString.Team;

    if (p1 && p2 && showPartner) return `${p1} + ${p2}`;
    return p1 || p2 || '--';
  }, [participantDetails, tournamentType]);

  const displayScore = useMemo(() => {
    return scoreData
      ? 'sets' in scoreData && scoreData.sets.length
        ? scoreData.sets?.join(' | ')
        : 'games' in scoreData && scoreData.games.length
          ? scoreData.games?.join(' | ')
          : '--'
      : '--';
  }, [scoreData]);

  const resetModal = () => {
    setIsDeclareWinnerModalOpen(false);
  };

  return (
    <Container>
      <Header>
        <Circle>{participantNumber}</Circle>
        <HeaderText>
          {tournamentType === TournamentTypeString.Singles ? 'Player' : 'Team'}{' '}
          {participantNumber}
        </HeaderText>
      </Header>
      <Info>
        <TeamBox>{teamName}</TeamBox>
        <ScoreRow>
          <Label>Score:</Label>
          <Score>{displayScore}</Score>
        </ScoreRow>
      </Info>
      {isMatchCompleted && isWinner && (
        <WinnerContainer>
          <TrophyIcon />
          <WinnerText>Winner</WinnerText>
        </WinnerContainer>
      )}

      {!isMatchCompleted && (
        <DeclareButton
          onClick={() => setIsDeclareWinnerModalOpen(true)}
          disabled={!participantDetails?.participantId || disableDeclare}
        >
          Declare Winner
        </DeclareButton>
      )}
      <DeclareModal
        title={'Declare Winner'}
        open={isDeclareWinnerModalOpen}
        onCancel={resetModal}
        minHeight={'auto'}
        removeFooterMargin={true}
      >
        <StyledText>
          {`Are you sure you want to declare ${teamName} as the winner? This
          action cannot be undone`}
        </StyledText>
        <ButtonSection>
          <CustomButton onClick={resetModal}>Cancel</CustomButton>
          <CustomButton
            type="primary"
            onClick={() => {
              participantDetails?.participantId &&
                onDeclaration({ winnerId: participantDetails.participantId });
              resetModal();
            }}
          >
            Declare Winner
          </CustomButton>
        </ButtonSection>
      </DeclareModal>
    </Container>
  );
};

export default ScoreCard;

/* ================= STYLES ================= */

const Container = styled.div`
  width: 100%;
  border: 0.5px solid ${COLORS.skyBlue.medium};
  padding: 50px 30px;
  border-radius: 14px;
  background: ${COLORS.skyBlue.secondary};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Circle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${COLORS.blue.primary};
  color: ${COLORS.white.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderText = styled.div`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 12px 0px;
`;

const TeamBox = styled.div`
  background: ${COLORS.white.primary};
  color: ${COLORS.black.primary};
  padding: 12px;
  border: 1px solid ${COLORS.grey.medium};
  border-radius: 10px;
  margin-bottom: 8px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  font-size: ${TEXT_SIZES.SM}px;
`;

const ScoreRow = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-start;
  align-items: center;
`;

const Label = styled.div`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

const Score = styled.div`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.LG}PX;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const DeclareButton = styled.button`
  width: 100%;
  height: 44px;
  margin-top: 12px;
  border-radius: 8px;
  border: none;
  background: ${COLORS.skyBlue.primary};
  color: ${COLORS.white.primary};
  cursor: pointer;
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};

  &:disabled {
    background: ${COLORS.grey.secondaryLight};
    cursor: not-allowed;
  }
`;

const WinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const TrophyIcon = styled(TrophyFilled)`
  font-size: ${TEXT_SIZES.THIRTEEN_XL}px;
  color: ${COLORS.orange.primary};
`;

const WinnerText = styled.div`
  font-size: ${TEXT_SIZES.LG}PX;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.skyBlue.primary};
`;

const StyledText = styled.p`
  width: 100%;
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 32px;
`;

const DeclareModal = styled(Modal)`
  top: 30%;
`;
